import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Link, Spinner } from '@structural-climbing/ui'
import { formatMoney } from '../products/money'

interface StripeBalance {
  connected: boolean
  currency?: string
  available?: number
  pending?: number
  next_payout?: { amount: number; arrival_date: string } | null
}

/** Day-first, the way the rest of the gym app writes dates. */
function formatDay(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language.startsWith('ro') ? 'ro-RO' : 'en-GB', {
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso))
}

/**
 * What the gym holds on Stripe, read live each time the tab opens.
 *
 * The gym's own Stripe dashboard is the full picture (every payment, refunds,
 * exports for the accountant); this is the three numbers an owner checks most,
 * plus the way in. Stripe offers no one-click login for full-dashboard
 * accounts, so the link opens its normal sign-in page.
 */
export function StripeBalanceCard() {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [balance, setBalance] = useState<StripeBalance | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/stripe/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error('balance')
        const body = (await res.json()) as StripeBalance
        if (!cancelled) setBalance(body)
      } catch {
        if (!cancelled) setFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiUrl, getAccessTokenSilently])

  const currency = (balance?.currency ?? 'ron').toUpperCase()
  const lei = (bani: number | undefined) => formatMoney((bani ?? 0) / 100, currency)

  return (
    <div className="rounded-medium border border-default-200 p-3">
      <p className="text-sm font-medium">{t('payments.balanceTitle')}</p>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      )}

      {!loading && failed && (
        <p className="mt-2 text-xs text-foreground/50">{t('payments.balanceError')}</p>
      )}

      {!loading && !failed && balance?.connected && (
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs text-foreground/50">{t('payments.balanceAvailable')}</p>
            <p className="text-lg font-semibold">{lei(balance.available)}</p>
          </div>
          <div>
            <p className="text-xs text-foreground/50">{t('payments.balancePending')}</p>
            <p className="text-lg font-semibold">{lei(balance.pending)}</p>
          </div>
          <div>
            <p className="text-xs text-foreground/50">{t('payments.balanceNextPayout')}</p>
            {balance.next_payout ? (
              <p className="text-lg font-semibold">
                {t('payments.balanceNextPayoutValue', {
                  amount: lei(balance.next_payout.amount),
                  date: formatDay(balance.next_payout.arrival_date, i18n.language),
                })}
              </p>
            ) : (
              <p className="text-sm text-foreground/50">{t('payments.balanceNoPayout')}</p>
            )}
          </div>
        </div>
      )}

      <Link
        className="mt-3"
        href="https://dashboard.stripe.com"
        isExternal
        showAnchorIcon
        size="sm"
      >
        {t('payments.openStripe')}
      </Link>
      <p className="text-xs text-foreground/50">{t('payments.openStripeHint')}</p>
    </div>
  )
}
